// SignUp.tsx — Signup Screen (Shopify Integration)
import { FC, useState } from 'react';
import { hooks } from '../hooks';
import { custom } from '../custom';
import { svg } from '../assets/svg';
import { theme } from '../constants';
import { components } from '../components';
import React from 'react'; // Ensure React is imported

// --- STORE CONFIGURATION ---
// IMPORTANT: Based on your clarification, we use this mapping:
const SHOPIFY_STORES = {
    // 1. USA Store
    USA: { 
        url: 'https://festivehq.myshopify.com/api/2025-07/graphql.json',
        token: '223e6f6958a6e1568db68d8891388212',
    },
    // 2. INDIA Store
    INDIA: { 
        url: 'https://in.festivehq.com/api/2025-07/graphql.json', 
        token: '7061d5be89513b8d01e171d44f83584c', 
    },
};
type StoreKey = keyof typeof SHOPIFY_STORES;

// --- Helper to determine Primary/Secondary stores based on location ---
// Note: Secondary store is theoretical now, but this helper remains useful.
const getLocationConfig = (userRegion: StoreKey) => {
    return {
        PRIMARY_STORE_KEY: userRegion,
        SECONDARY_STORE_KEY: userRegion === 'INDIA' ? 'USA' : 'INDIA',
    };
};

// --- Define Shopify Error Type ---
interface CustomerUserError {
    field?: string[];
    message: string;
}

const renderHeader = (): JSX.Element => {
    return <components.Header goBack={true} />;
};

export const SignUp: FC = (): JSX.Element => {
    const navigate = hooks.useAppNavigate();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // State for user's region choice, defaults to USA
    const [userRegion, setUserRegion] = useState<StoreKey>('USA'); 

    // Dynamic store configuration based on user choice
    const { PRIMARY_STORE_KEY } = getLocationConfig(userRegion);
    const PRIMARY_CONFIG = SHOPIFY_STORES[PRIMARY_STORE_KEY];


    // --- Email format validator ---
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // --- HANDLE SIGN UP: Targets only the user's selected store ---
    const handleSignUp = async () => {
        if (!firstName || !lastName || !email || !password || !confirmPassword || !userRegion) {
            alert('All fields are required, including your region selection.');
            return;
        }

        if (!isValidEmail(email)) {
            alert('Please enter a valid email address.');
            return;
        }

        if (password !== confirmPassword) {
            alert('Passwords do not match.');
            return;
        }
        
        setLoading(true);

        const query = `
            mutation customerCreate($input: CustomerCreateInput!) {
                customerCreate(input: $input) {
                    customer {
                        id
                        email
                        firstName
                        lastName
                    }
                    customerUserErrors {
                        field
                        message
                    }
                }
            }
        `;

        const variables = {
            input: {
                firstName,
                lastName,
                email,
                password,
            },
        };

        try {
            // Target the dynamically determined PRIMARY store (based on userRegion state)
            const response = await fetch(PRIMARY_CONFIG.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': PRIMARY_CONFIG.token, 
                },
                body: JSON.stringify({ query, variables }),
            });

            const result = await response.json();
            const customerCreateData = result.data.customerCreate;

            if (customerCreateData.customer) {
                // SUCCESS on the SELECTED store: Immediately set session context
                
                // Save user details locally 
                localStorage.setItem('user', JSON.stringify({ 
                    firstName, 
                    lastName, 
                    email,
                    loggedInStoreKey: PRIMARY_STORE_KEY
                }));

                // Set the session flags for immediate login
                localStorage.setItem('isLoggedIn', 'true');
                localStorage.setItem('loggedInStoreKey', PRIMARY_STORE_KEY); 
                
                alert(`Account successfully created for the **${PRIMARY_STORE_KEY}** store! You are now signed in.`);
                
                // Redirect to the main page
                navigate('/', { replace: true }); 

            } else {
                const errors: CustomerUserError[] = customerCreateData.customerUserErrors;
                
                // Check for specific email already registered error
                const emailError = errors?.find(
                    (e: CustomerUserError) =>
                        e.field?.includes('email') &&
                        (e.message.toLowerCase().includes('already in use') ||
                          e.message.toLowerCase().includes('taken'))
                );

                if (emailError) {
                    alert(`Email is already registered on the **${PRIMARY_STORE_KEY}** store. Please sign in.`);
                } else {
                    const genericError = errors?.[0]?.message;
                    alert(`Sign up failed: ${genericError || 'Something went wrong'}`);
                }
            }
        } catch (err) {
            console.error(err);
            alert('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderContent = (): JSX.Element => {
        return (
            <div style={{ padding: '50px 20px 20px 20px' }}>
                <h1
                    style={{
                        margin: 0,
                        textAlign: 'center',
                        ...theme.fonts.Mulish_700Bold,
                        color: theme.colors.mainColor,
                        fontSize: 32,
                        lineHeight: 1.2,
                        textTransform: 'capitalize',
                        marginBottom: 40,
                    }}
                >
                    Sign up
                </h1>
                
                {/* REGION SELECTOR */}
                <div style={{ marginBottom: 20 }}>
                    <label style={{ 
                        ...theme.fonts.Mulish_600SemiBold, 
                        color: theme.colors.textColor, 
                        fontSize: 14, 
                        display: 'block',
                        marginBottom: 8
                    }}>
                        Select Your Primary Region
                    </label>
                    <select
                        value={userRegion}
                        onChange={(e) => setUserRegion(e.target.value as StoreKey)}
                        style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: `1px solid #696565ff`,
                            borderRadius: '10px',
                            backgroundColor:'#ffffffff',
                            fontSize: 16,
                            ...theme.fonts.Mulish_400Regular,
                            appearance: 'none', 
                            cursor: 'pointer',
                        }}
                    >
                        <option value="USA">🇺🇸 USA Store</option>
                        <option value="INDIA">🇮🇳 INDIA Store</option>
                    </select>
                </div>
                
                <p style={{ textAlign: 'center', color: theme.colors.mainColor, marginBottom: 20, ...theme.fonts.Mulish_600SemiBold }}>
                    You are creating your account on the **{PRIMARY_STORE_KEY}** store.
                </p>

                {/* Input Fields (First Name, Last Name, Email, Password, Confirm Password) */}
                <custom.InputField
                    label="First Name"
                    placeholder="Zenith"
                    icon={<svg.InputCheckSvg />}
                    containerStyle={{ marginBottom: 20 }}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                />

                <custom.InputField
                    label="Last Name"
                    placeholder="Sneaks"
                    icon={<svg.InputCheckSvg />}
                    containerStyle={{ marginBottom: 20 }}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                />

                <custom.InputField
                    label="Email"
                    icon={<svg.InputCheckSvg />}
                    placeholder="zenithsneaks@mail.com"
                    containerStyle={{ marginBottom: 20 }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />

                <custom.InputField
                    clickable={true}
                    label="Password"
                    placeholder="••••••••"
                    type={showPassword ? 'text' : 'password'}
                    containerStyle={{ marginBottom: 20 }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />

                <custom.InputField
                    clickable={true}
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm Password"
                    placeholder="••••••••"
                    containerStyle={{ marginBottom: 20 }}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <components.Button 
                    title={loading ? 'Creating Account...' : 'Sign up'} 
                    onClick={handleSignUp} 
                    style={{ marginBottom: 20 }} 
                    disabled={loading}
                />

                <div style={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
                    <span
                        style={{
                            marginRight: 4,
                            ...theme.fonts.Mulish_400Regular,
                            color: theme.colors.textColor,
                            fontSize: 16,
                            lineHeight: 1.7,
                        }}
                    >
                        Already have an account?
                    </span>
                    <span
                        style={{
                            ...theme.fonts.Mulish_400Regular,
                            color: theme.colors.mainColor,
                            fontSize: 16,
                            lineHeight: 1.7,
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate('/SignIn')}
                    >
                        Sign in.
                    </span>
                </div>
            </div>
        );
    };

    const renderBottomTabBar = (): JSX.Element => {
        return <components.BottomTabBar />;
    };

    return (
        <>
            {renderHeader()}
            {renderContent()}
            {renderBottomTabBar()}
        </>
    );
};